import { z } from 'zod'

export const MyceliumEventPriority = z.enum(['critical', 'high', 'normal', 'low'])
export type MyceliumEventPriority = z.infer<typeof MyceliumEventPriority>

/**
 * Worker-contract event shape.
 *
 * Used by external workers (Cloud Run, Go, Deno Edge Function) that poll
 * PGMQ or receive Pub/Sub pushes. Distinct from the in-app backbone event
 * shape ({@link BackboneEvent} in `backbone.ts`).
 *
 * Version field enables non-breaking schema evolution. Must bump version
 * if payload structure changes. Consumers MUST handle version mismatch gracefully.
 *
 * Current version: 1 (locked, 2026-05-05)
 */
export const MyceliumWorkerEvent = z.object({
  version: z.literal(1),        // Schema version — must be 1 for current envelope
  eventId: z.string().uuid(),
  domain: z.string(),           // e.g. "cortex.session", "xero.sync", "ai.memory"
  action: z.string(),           // e.g. "acquire", "release", "heartbeat"
  payload: z.record(z.unknown()),
  correlationId: z.string().uuid().optional(),   // links related events
  causationId: z.string().uuid().optional(),     // the event that caused this one
  priority: MyceliumEventPriority.default('normal'),
  publishedAt: z.string().datetime(),
  retryCount: z.number().int().min(0).default(0),
  maxRetries: z.number().int().min(0).default(3),
})
export type MyceliumWorkerEvent = z.infer<typeof MyceliumWorkerEvent>

/**
 * Schema version constant — reference for consumers validating against known version.
 * Current lock: version 1 (2026-05-05).
 */
export const MYCELIUM_SCHEMA_VERSION = 1 as const

/**
 * @deprecated Use {@link MyceliumWorkerEvent} instead.
 * Kept for backward compatibility with existing imports.
 */
export const MyceliumEvent = MyceliumWorkerEvent
/** @deprecated Use {@link MyceliumWorkerEvent} instead. */
export type MyceliumEvent = MyceliumWorkerEvent
